// src/theme.js - Light and Dark Theme with Toggle Support
// Version 3.1 - PITCH DARK MODE (Pure Black)

import { createTheme } from '@mui/material/styles';

// ============================================
// LIGHT THEME (Default - White)
// ============================================

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0ea5a4',
      light: '#2cc2c1',
      dark: '#0b8180',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f4f8ff',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
      disabled: '#bdbdbd',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#ffffff',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#ffffff',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#ffffff',
    },
    divider: '#e0e0e0',
  },
  typography: {
    fontFamily: '"Manrope", "Space Grotesk", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: 'clamp(1.5rem, 4vw, 2rem)',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: 'clamp(1.3rem, 3.5vw, 1.75rem)',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: 'clamp(1.1rem, 3vw, 1.5rem)',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
      fontWeight: 400,
      lineHeight: 1.75,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
    },
    body1: {
      fontSize: 'clamp(0.9rem, 1.5vw, 1rem)',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.66,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 2.66,
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 14,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.08)',
    '0px 4px 8px rgba(0,0,0,0.08)',
    '0px 6px 12px rgba(0,0,0,0.08)',
    '0px 8px 16px rgba(0,0,0,0.08)',
    '0px 10px 20px rgba(0,0,0,0.08)',
    '0px 12px 24px rgba(0,0,0,0.08)',
    '0px 14px 28px rgba(0,0,0,0.08)',
    '0px 1px 3px rgba(0,0,0,0.12)',
    '0px 1px 5px rgba(0,0,0,0.12)',
    '0px 1px 8px rgba(0,0,0,0.12)',
    '0px 2px 6px rgba(0,0,0,0.12)',
    '0px 3px 8px rgba(0,0,0,0.12)',
    '0px 4px 10px rgba(0,0,0,0.12)',
    '0px 5px 12px rgba(0,0,0,0.12)',
    '0px 6px 14px rgba(0,0,0,0.12)',
    '0px 7px 16px rgba(0,0,0,0.12)',
    '0px 8px 18px rgba(0,0,0,0.12)',
    '0px 9px 20px rgba(0,0,0,0.12)',
    '0px 10px 22px rgba(0,0,0,0.12)',
    '0px 11px 24px rgba(0,0,0,0.12)',
    '0px 12px 26px rgba(0,0,0,0.12)',
    '0px 13px 28px rgba(0,0,0,0.12)',
    '0px 14px 30px rgba(0,0,0,0.12)',
    '0px 15px 32px rgba(0,0,0,0.12)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f4f8ff',
          color: '#212121',
          scrollbarColor: '#bdbdbd #ffffff',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '8px',
            backgroundColor: '#bdbdbd',
            minHeight: '24px',
            '&:hover': {
              backgroundColor: '#9e9e9e',
            },
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f5f5f5',
          },
        },
        root: {
          minHeight: '100vh',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: 'medium',
      },
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 18px',
          minHeight: '44px',
          transition: 'all 0.2s ease',
          '&:active': {
            transform: 'scale(0.98)',
          },
          '@media (max-width: 600px)': {
            padding: '9px 14px',
            minHeight: '44px',
            fontSize: '0.875rem',
          },
        },
        contained: {
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0,0,0,0.15)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.86)',
          backgroundImage: 'none',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(151, 171, 198, 0.2)',
          borderRadius: '16px',
        },
        elevation1: {
          boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
        },
        elevation2: {
          boxShadow: '0px 4px 8px rgba(0,0,0,0.08)',
        },
        elevation3: {
          boxShadow: '0px 6px 12px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#212121',
          boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e0e0e0',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#f5f5f5',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e0e0e0',
          padding: '12px 10px',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#f5f5f5',
          color: '#212121',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1976d2',
            },
          },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiDialog: {
      defaultProps: {
        fullWidth: true,
        maxWidth: 'md',
      },
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          boxShadow: '0px 8px 24px rgba(0,0,0,0.15)',
          borderRadius: '18px',
          width: 'min(96vw, 980px)',
          maxHeight: '90vh',
          margin: '16px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px 10px',
          fontWeight: 700,
          lineHeight: 1.3,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '14px 24px 20px',
        },
        dividers: {
          padding: '16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: '10px',
          justifyContent: 'flex-end',
          alignItems: 'center',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflowX: 'auto',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontWeight: 600,
        },
      },
    },
    MuiStepContent: {
      styleOverrides: {
        root: {
          marginLeft: '14px',
          paddingLeft: '20px',
          borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#424242',
          fontSize: '0.875rem',
          borderRadius: '4px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
  },
});

// ============================================
// PITCH DARK THEME (Pure Black)
//  FIXED: Changed from bluish dark to pitch black
// ============================================

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#ffffff',
      light: '#f4f4f5',
      dark: '#d4d4d8',
      contrastText: '#000000',
    },
    secondary: {
      main: '#a1a1aa',
      light: '#d4d4d8',
      dark: '#71717a',
      contrastText: '#ffffff',
    },
    background: {
      default: '#09090b',
      paper: '#18181b',
    },
    text: {
      primary: '#fafafa',
      secondary: '#a1a1aa',
      disabled: '#52525b',
    },
    success: {
      main: '#22c55e',
      light: '#4ade80',
      dark: '#16a34a',
      contrastText: '#ffffff',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#ffffff',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
      contrastText: '#ffffff',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: '"Manrope", "Space Grotesk", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 700,
      fontSize: '1.125rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.75,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.66,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 2.66,
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 14,
  },
  shadows: [
    'none',
    '0px 2px 8px rgba(0,0,0,0.3)',
    '0px 4px 12px rgba(0,0,0,0.3)',
    '0px 6px 16px rgba(0,0,0,0.3)',
    '0px 8px 20px rgba(0,0,0,0.3)',
    '0px 10px 24px rgba(0,0,0,0.3)',
    '0px 12px 28px rgba(0,0,0,0.3)',
    '0px 14px 32px rgba(0,0,0,0.3)',
    '0px 1px 5px rgba(0,0,0,0.4)',
    '0px 1px 8px rgba(0,0,0,0.4)',
    '0px 1px 10px rgba(0,0,0,0.4)',
    '0px 2px 8px rgba(0,0,0,0.4)',
    '0px 3px 10px rgba(0,0,0,0.4)',
    '0px 4px 12px rgba(0,0,0,0.4)',
    '0px 5px 14px rgba(0,0,0,0.4)',
    '0px 6px 16px rgba(0,0,0,0.4)',
    '0px 7px 18px rgba(0,0,0,0.4)',
    '0px 8px 20px rgba(0,0,0,0.4)',
    '0px 9px 22px rgba(0,0,0,0.4)',
    '0px 10px 24px rgba(0,0,0,0.4)',
    '0px 11px 26px rgba(0,0,0,0.4)',
    '0px 12px 28px rgba(0,0,0,0.4)',
    '0px 13px 30px rgba(0,0,0,0.4)',
    '0px 14px 32px rgba(0,0,0,0.4)',
    '0px 15px 34px rgba(0,0,0,0.4)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          backgroundColor: '#09090b',
        },
        body: {
          backgroundColor: '#09090b',
          color: '#fafafa',
          scrollbarColor: '#27272a #09090b',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '8px',
            backgroundColor: '#27272a',
            minHeight: '24px',
            '&:hover': {
              backgroundColor: '#3f3f46',
            },
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#09090b',
          },
        },
        root: {
          minHeight: '100vh',
          backgroundColor: '#09090b',
        },
        '#root': {
          minHeight: '100vh',
          backgroundColor: '#09090b',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: 'medium',
      },
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 18px',
          minHeight: '44px',
          transition: 'all 0.2s ease',
          '&:active': {
            transform: 'scale(0.98)',
          },
          '@media (max-width: 600px)': {
            padding: '9px 14px',
            minHeight: '44px',
            fontSize: '0.875rem',
          },
        },
        contained: {
          backgroundImage: 'linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 100%)',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3), inset 0px 1px 0px rgba(255, 255, 255, 0.5)',
          border: '1px solid rgba(0, 0, 0, 0.2)',
          '&:hover': {
            boxShadow: '0px 6px 14px rgba(0, 0, 0, 0.4), inset 0px 1px 0px rgba(255, 255, 255, 0.6)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(24, 24, 27, 0.65)',
          backgroundImage: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderTop: '1px solid rgba(255, 255, 255, 0.12)',
          borderLeft: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
          borderRadius: '16px',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.4)',
            borderTop: '1px solid rgba(255, 255, 255, 0.16)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.16)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(24, 24, 27, 0.65)',
          backgroundImage: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderTop: '1px solid rgba(255, 255, 255, 0.12)',
          borderLeft: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
          '@media (max-width: 600px)': {
            borderRadius: '8px',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px',
          '@media (max-width: 600px)': {
            padding: '16px',
            '&:last-child': {
              paddingBottom: '16px',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(9, 9, 11, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: '#09090b',
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#18181b',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          padding: '12px 8px',
          '@media (max-width: 600px)': {
            padding: '8px 6px',
            fontSize: '0.75rem',
          },
        },
        head: {
          fontWeight: 700,
          backgroundColor: '#18181b',
          color: '#fafafa',
          '@media (max-width: 600px)': {
            fontSize: '0.75rem',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#ffffff',
            },
          },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiDialog: {
      defaultProps: {
        fullWidth: true,
        maxWidth: 'md',
      },
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(24, 24, 27, 0.85)',
          backgroundImage: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderTop: '1px solid rgba(255, 255, 255, 0.15)',
          borderLeft: '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: '0px 24px 48px rgba(0, 0, 0, 0.6)',
          borderRadius: '16px',
          width: 'min(96vw, 980px)',
          maxHeight: '90vh',
          margin: '16px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px 10px',
          fontWeight: 700,
          lineHeight: 1.3,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '14px 24px 20px',
        },
        dividers: {
          padding: '16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: '10px',
          justifyContent: 'flex-end',
          alignItems: 'center',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflowX: 'auto',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontWeight: 600,
        },
      },
    },
    MuiStepContent: {
      styleOverrides: {
        root: {
          marginLeft: '14px',
          paddingLeft: '20px',
          borderLeft: '1px solid rgba(255, 255, 255, 0.16)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(10, 10, 10, 0.95)',  //  Dark gray
          border: '1px solid rgba(255, 255, 255, 0.1)',
          fontSize: '0.875rem',
          borderRadius: '8px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
  },
});

// Default export is light theme (white)
export default lightTheme;
